import { useCallback, useEffect, useRef, useState } from 'react'
import { recordCheckin } from '@/lib/checkin'
import type { LogType } from '@/lib/canonicalMessage'
import { syncOpenAuftraege } from '@/lib/auftrag'
import type { CachedAuftrag } from '@/lib/db'
import { getDevice } from '@/lib/db'
import {
  getCheckinStatus,
  getTodayCheckins,
  type CheckinStatusResponse,
  type TodayCheckin,
} from '@/lib/api'

const nfcSupported = typeof window !== 'undefined' && 'NDEFReader' in window
const AUFTRAG_SYNC_INTERVAL_MS = 60_000
const RAPPORT_REFRESH_MS = 15_000

interface Props {
  onAuftragAbschliessen: (auftrag: CachedAuftrag) => void
}

function formatDuration(fromIso: string): string {
  const ms = Date.now() - new Date(fromIso).getTime()
  const totalMin = Math.floor(ms / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `${h}h ${m}min` : `${m}min`
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

const LOG_TYPE_LABEL: Record<string, string> = {
  IN: 'Kommen',
  OUT: 'Gehen',
  ANFAHRT: 'Anfahrt',
  ABFAHRT: 'Abfahrt',
}

export function KioskPage({ onAuftragAbschliessen }: Props) {
  const [pin, setPin] = useState('')
  const [confirmation, setConfirmation] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nfcActive, setNfcActive] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const [auftraege, setAuftraege] = useState<CachedAuftrag[]>([])
  const [auftragId, setAuftragId] = useState<string>('')
  const [taetigkeit, setTaetigkeit] = useState('')
  const [zusatzgeraet, setZusatzgeraet] = useState('')

  // Status nach PIN-Eingabe: aktueller IN/OUT-Stand des Mitarbeiters
  const [employeeStatus, setEmployeeStatus] = useState<CheckinStatusResponse | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)

  // Rapportzettel: heutige Eintraege fuer den gewahlten Auftrag
  const [todayEntries, setTodayEntries] = useState<TodayCheckin[]>([])
  const [, setNow] = useState(Date.now()) // fuer laufende Timer-Anzeige

  const selectedAuftrag = auftraege.find((a) => a.auftragId === auftragId)

  // Auftraege-Sync
  useEffect(() => {
    let cancelled = false
    async function refresh() {
      const result = await syncOpenAuftraege()
      if (!cancelled) setAuftraege(result)
    }
    refresh()
    const interval = setInterval(refresh, AUFTRAG_SYNC_INTERVAL_MS)
    return () => { cancelled = true; clearInterval(interval) }
  }, [])

  // Laufende Uhr: jede Minute neu rendern
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(interval)
  }, [])

  // NFC cleanup
  useEffect(() => () => abortRef.current?.abort(), [])

  // Rapportzettel laden wenn Auftrag gewaehlt
  const refreshRapport = useCallback(async () => {
    if (!auftragId) { setTodayEntries([]); return }
    try {
      const device = await getDevice()
      if (!device) return
      const entries = await getTodayCheckins(device.deviceId, auftragId)
      setTodayEntries(entries)
    } catch {
      // Rapport-Fehler still ignorieren -- kein Grund die ganze Seite zu blockieren
    }
  }, [auftragId])

  useEffect(() => {
    refreshRapport()
    const interval = setInterval(refreshRapport, RAPPORT_REFRESH_MS)
    return () => clearInterval(interval)
  }, [refreshRapport])

  // Status laden sobald PIN komplett (4+ Zeichen) und Fokus verloren oder Enter
  async function loadStatus(identifier: string) {
    if (!identifier) { setEmployeeStatus(null); return }
    setStatusLoading(true)
    try {
      const device = await getDevice()
      if (!device) { setStatusLoading(false); return }
      const status = await getCheckinStatus(device.deviceId, identifier)
      setEmployeeStatus(status)
      // Tätigkeit vorbelegen: beim Kommen das Gewerk des Auftrags
      if (!status.is_checked_in && selectedAuftrag && !taetigkeit) {
        setTaetigkeit(selectedAuftrag.gewerk ?? '')
      }
    } catch {
      setEmployeeStatus(null)
    } finally {
      setStatusLoading(false)
    }
  }

  async function submit(logType: LogType, employeeIdentifier: string) {
    setError(null)
    try {
      await recordCheckin(employeeIdentifier, logType, {
        auftragId: auftragId || undefined,
        taetigkeitGeplant: logType === 'IN' ? taetigkeit || undefined : undefined,
        taetigkeitIst: logType === 'OUT' ? taetigkeit || undefined : undefined,
        zusatzgeraet: auftragId ? zusatzgeraet || undefined : undefined,
      })
      setConfirmation(`${LOG_TYPE_LABEL[logType]} erfasst — ${employeeIdentifier}`)
      setPin('')
      setTaetigkeit('')
      setZusatzgeraet('')
      setEmployeeStatus(null)
      setTimeout(() => setConfirmation(null), 3000)
      refreshRapport()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function startNfcScan(logType: LogType) {
    if (!nfcSupported) return
    setError(null)
    setNfcActive(true)
    try {
      abortRef.current = new AbortController()
      // @ts-expect-error -- NDEFReader ist bei TypeScript noch nicht in lib.dom.d.ts
      const reader = new window.NDEFReader()
      await reader.scan({ signal: abortRef.current.signal })
      reader.onreading = async (event: { message: { records: { recordType: string; data: DataView }[] } }) => {
        const record = event.message.records.find((r) => r.recordType === 'text')
        if (!record) return
        const identifier = new TextDecoder().decode(record.data).trim()
        setNfcActive(false)
        abortRef.current?.abort()
        await submit(logType, identifier)
      }
    } catch (e) {
      setNfcActive(false)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const isCheckedIn = employeeStatus?.is_checked_in ?? false
  const lastTime = employeeStatus?.last_time ?? null

  return (
    <div className="screen">
      <div className="card">
        <h1>Stempeln</h1>

        {confirmation && <p className="confirmation">{confirmation}</p>}
        {error && <p className="error-text">{error}</p>}

        {/* Auftrag */}
        <select
          className="input"
          value={auftragId}
          onChange={(e) => { setAuftragId(e.target.value); setTaetigkeit('') }}
        >
          <option value="">Kein Auftrag (nur Kommen/Gehen)</option>
          {auftraege.map((a) => (
            <option key={a.auftragId} value={a.auftragId}>
              {a.auftragsnummer} — {a.auftraggeberName}
            </option>
          ))}
        </select>

        {selectedAuftrag && (
          <button
            className="button button-secondary"
            onClick={() => onAuftragAbschliessen(selectedAuftrag)}
          >
            Auftrag abschließen (Unterschriften)
          </button>
        )}

        {/* PIN */}
        {nfcSupported && (
          <p className="hint">{nfcActive ? 'Karte an das Gerät halten …' : 'Karte auflegen oder PIN eingeben'}</p>
        )}
        <input
          className="input"
          type="password"
          inputMode="numeric"
          placeholder="PIN"
          value={pin}
          onChange={(e) => { setPin(e.target.value); setEmployeeStatus(null) }}
          onBlur={(e) => loadStatus(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadStatus(pin)}
        />

        {/* Status-Anzeige: laufende Zeit oder Bereitschaft */}
        {pin && !statusLoading && employeeStatus && (
          <div className={`status-badge ${isCheckedIn ? 'status-in' : 'status-out'}`}>
            {isCheckedIn && lastTime ? (
              <>
                <span className="status-dot" />
                Eingestempelt seit {formatTime(lastTime)}
                <span className="status-duration"> ({formatDuration(lastTime)})</span>
              </>
            ) : (
              <>
                <span className="status-dot" />
                Ausgestempelt
              </>
            )}
          </div>
        )}
        {statusLoading && <p className="hint">Lade Status …</p>}

        {/* Tätigkeit + Zusatzgerät */}
        <input
          className="input"
          placeholder={isCheckedIn ? 'Was wurde gemacht?' : 'Geplante Tätigkeit'}
          value={taetigkeit}
          onChange={(e) => setTaetigkeit(e.target.value)}
        />
        {selectedAuftrag && (
          <input
            className="input"
            placeholder="Zusatzgerät (z.B. Rüttelplatte, Stampfer)"
            value={zusatzgeraet}
            onChange={(e) => setZusatzgeraet(e.target.value)}
          />
        )}

        {/* Haupt-Aktionen: Kommen/Gehen hervorgehoben je nach Status */}
        <div className="button-row">
          <button
            className={`button ${isCheckedIn ? 'button-secondary' : 'button-in button-primary-action'}`}
            disabled={!pin}
            onClick={() => submit('IN', pin)}
          >
            ↓ Kommen
          </button>
          <button
            className={`button ${isCheckedIn ? 'button-out button-primary-action' : 'button-secondary'}`}
            disabled={!pin}
            onClick={() => submit('OUT', pin)}
          >
            ↑ Gehen
          </button>
        </div>
        {nfcSupported && (
          <div className="button-row">
            <button className="button button-secondary" onClick={() => startNfcScan('IN')}>NFC: Kommen</button>
            <button className="button button-secondary" onClick={() => startNfcScan('OUT')}>NFC: Gehen</button>
          </div>
        )}
      </div>

      {/* Rapportzettel: heutige Eintraege fuer den gewahlten Auftrag */}
      {selectedAuftrag && todayEntries.length > 0 && (
        <div className="card rapport-card">
          <h2>Heute — {selectedAuftrag.auftragsnummer}</h2>
          <table className="rapport-table">
            <thead>
              <tr>
                <th>PIN</th>
                <th>Ereignis</th>
                <th>Zeit</th>
                <th>Tätigkeit</th>
                <th>Zusatz</th>
              </tr>
            </thead>
            <tbody>
              {todayEntries.map((e) => (
                <tr key={e.event_id} className={`rapport-row rapport-${e.log_type.toLowerCase()}`}>
                  <td>{e.employee_identifier}</td>
                  <td>{LOG_TYPE_LABEL[e.log_type] ?? e.log_type}</td>
                  <td>{formatTime(e.client_time)}</td>
                  <td>{e.taetigkeit_geplant ?? e.taetigkeit_ist ?? ''}</td>
                  <td>{e.zusatzgeraet ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
